import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { styled } from '@mui/material/styles';
import LoaderProgress from '../components/ui/loader-progress';
import { toast } from 'react-toastify';
import api from '../services/api';
import { Search } from 'lucide-react';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

export default function LorenzoUsers() {
  const [list, setList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterName, setFilterName] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterOffice, setFilterOffice] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchData = async () => {
    try {
      const [usersRes, deptRes, officeRes] = await Promise.all([
        api.get('/lorenzo-users'),
        api.get('/department'),
        api.get('/office')
      ]);
      setList(usersRes.data);
      setDepartments(deptRes.data);
      setOffices(officeRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleFilterDepartment = (event) => {
    setFilterDepartment(event.target.value);
    setPage(0);
  };

  const handleFilterOffice = (event) => {
    setFilterOffice(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredList = list
    .filter((item) => {
      // Filter by department
      if (filterDepartment !== 'all' && item.rif_department !== parseInt(filterDepartment)) {
        return false;
      }
      // Filter by office
      if (filterOffice !== 'all' && item.rif_office !== parseInt(filterOffice)) {
        return false;
      }

      if (!filterName) return true;
      const search = filterName.toLowerCase();
      return (
        (item.name && item.name.toLowerCase().includes(search)) ||
        (item.surname && item.surname.toLowerCase().includes(search)) ||
        (item.email && item.email.toLowerCase().includes(search)) ||
        (item.whatsapp_number && item.whatsapp_number.toLowerCase().includes(search)) ||
        (item.department_title && item.department_title.toLowerCase().includes(search)) ||
        (item.office_title && item.office_title.toLowerCase().includes(search))
      );
    })
    .sort((a, b) => {
      const isAsc = order === 'asc';
      if (orderBy === 'id' || orderBy === 'rif_department' || orderBy === 'rif_office') {
        return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
      }
      const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
      const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
      if (valA < valB) return isAsc ? -1 : 1;
      if (valA > valB) return isAsc ? 1 : -1;
      return 0;
    });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          Utenti (App Lorenzo)
        </Typography>
      </Box>

      <StyledCard>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            size="small"
            value={filterName}
            onChange={handleFilterByName}
            placeholder="Cerca per nome, cognome..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
            sx={{ maxWidth: 300 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Dipartimento</InputLabel>
            <Select
              value={filterDepartment}
              label="Dipartimento"
              onChange={handleFilterDepartment}
            >
              <MenuItem value="all">Tutti</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Ufficio</InputLabel>
            <Select
              value={filterOffice}
              label="Ufficio"
              onChange={handleFilterOffice}
            >
              <MenuItem value="all">Tutti</MenuItem>
              {offices.map((office) => (
                <MenuItem key={office.id} value={office.id}>
                  {office.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => handleRequestSort('id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? order : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Nome
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'surname'}
                    direction={orderBy === 'surname' ? order : 'asc'}
                    onClick={() => handleRequestSort('surname')}
                  >
                    Cognome
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'email'}
                    direction={orderBy === 'email' ? order : 'asc'}
                    onClick={() => handleRequestSort('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'whatsapp_number'}
                    direction={orderBy === 'whatsapp_number' ? order : 'asc'}
                    onClick={() => handleRequestSort('whatsapp_number')}
                  >
                    Whatsapp
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'rif_department'}
                    direction={orderBy === 'rif_department' ? order : 'asc'}
                    onClick={() => handleRequestSort('rif_department')}
                  >
                    Rif. Dip.
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'rif_office'}
                    direction={orderBy === 'rif_office' ? order : 'asc'}
                    onClick={() => handleRequestSort('rif_office')}
                  >
                    Rif. Uff.
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'created_at'}
                    direction={orderBy === 'created_at' ? order : 'asc'}
                    onClick={() => handleRequestSort('created_at')}
                  >
                    Data Creazione
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <LoaderProgress size={50} />
                  </TableCell>
                </TableRow>
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun dato trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.id}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{item.name}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{item.surname}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.whatsapp_number}</TableCell>
                    <TableCell>
                      {item.rif_department}
                      {item.department_title && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {item.department_title}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.rif_office}
                      {item.office_title && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {item.office_title}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>
    </Box>
  );
}
